import { createReducer, createAction, PayloadAction } from '@reduxjs/toolkit'
import { Slide } from '../types'

export interface ManageSlidesReducer {
  slides: Record<string, Slide[]>
  activeSlide: Slide | null
  uploadedImage: string | null
}

const initialState: ManageSlidesReducer = {
  slides: {},
  activeSlide: null,
  uploadedImage: null
}

interface FetchSlidesPayload {
  lessonPlanId: string
}
export type FetchSlidesAction = PayloadAction<FetchSlidesPayload>
export const FETCH_SLIDES = createAction<FetchSlidesPayload>('manage_slides_reducer/fetch_slides')
interface FetchSlidesSuccessPayload {
  lessonPlanId: string
  slides: Slide[]
}
export type FetchSlidesSuccessAction = PayloadAction<FetchSlidesSuccessPayload>
export const FETCH_SLIDES_SUCCESS = createAction<FetchSlidesSuccessPayload>(
  'manage_slides_reducer/fetch_slides_success'
)
export const FETCH_SLIDES_FAIL = createAction('manage_slides_reducer/fetch_slides_fail')

interface CreateSlidePayload {
  lessonPlanId: string
}
export type CreateSlideAction = PayloadAction<CreateSlidePayload>
export const CREATE_SLIDE = createAction<CreateSlidePayload>('manage_slides_reducer/create_slide')
interface CreateSlideSuccessPayload {
  lessonPlanId: string
  slide: Slide
}
export type CreateSlideSuccessAction = PayloadAction<CreateSlideSuccessPayload>
export const CREATE_SLIDE_SUCCESS = createAction<CreateSlideSuccessPayload>(
  'manage_slides_reducer/create_slide_success'
)
export const CREATE_SLIDE_FAIL = createAction('manage_slides_reducer/create_slide_fail')

interface ReorderSlidesPayload {
  lessonPlanId: string
  ids: number[]
}
export type ReorderSlidesAction = PayloadAction<ReorderSlidesPayload>
export const REORDER_SLIDES = createAction<ReorderSlidesPayload>('manage_slides_reducer/reorder_slides')
interface ReorderSlidesSuccessPayload {
  lessonPlanId: string
  slides: Slide[]
}
export const REORDER_SLIDES_SUCCESS = createAction<ReorderSlidesSuccessPayload>(
  'manage_slides_reducer/reorder_slides_success'
)
export const REORDER_SLIDES_FAIL = createAction('manage_slides_reducer/reorder_slides_fail')

export const SET_ACTIVE_SLIDE = createAction<Slide>('manage_slides_reducer/set_active_slide')

export const SET_SLIDE_CONTENT = createAction<string>('manage_slides_reducer/set_slide_content')

interface SaveSlidePayload {
  lessonPlanId: string
}
export type SaveSlideAction = PayloadAction<SaveSlidePayload>
export const SAVE_SLIDE = createAction<SaveSlidePayload>('manage_slides_reducer/save_slide')
interface SaveSlideSuccessPayload {
  lessonPlanId: string
  slide: Slide
}
export const SAVE_SLIDE_SUCCESS = createAction<SaveSlideSuccessPayload>('manage_slides_reducer/save_slide_success')
export const SAVE_SLIDE_FAIL = createAction('manage_slides_reducer/save_slide_fail')

interface UploadImagePayload {
  base64Image: string
}
export type UploadImageAction = PayloadAction<UploadImagePayload>
export const UPLOAD_IMAGE = createAction<UploadImagePayload>('manage_slides_reducer/upload_image')
interface UploadImageSuccessPayload {
  imageUrl: string
}
export const UPLOAD_IMAGE_SUCCESS = createAction<UploadImageSuccessPayload>(
  'manage_slides_reducer/upload_image_success'
)
export const UPLOAD_IMAGE_FAIL = createAction('manage_slides_reducer/upload_image_fail')
export const CLEAR_UPLOADED_IMAGE = createAction('manage_slides_reducer/clear_uploaded_image')

interface RemoveSlidePayload {
  lessonPlanId: string
}
export type RemoveSlideAction = PayloadAction<RemoveSlidePayload>
export const REMOVE_SLIDE = createAction<RemoveSlidePayload>('manage_slides_reducer/remove_slide')
export const REMOVE_SLIDE_SUCCESS = createAction('manage_slides_reducer/remove_slide_success')
export const REMOVE_SLIDE_FAIL = createAction('manage_slides_reducer/remove_slide_fail')

export const manageSlidesReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(FETCH_SLIDES_SUCCESS, (state, action) => {
      return { ...state, slides: { ...state.slides, [action.payload.lessonPlanId]: action.payload.slides } }
    })
    .addCase(CREATE_SLIDE_SUCCESS, (state, action) => {
      return {
        ...state,
        slides: {
          ...state.slides,
          [action.payload.lessonPlanId]: [...state.slides[action.payload.lessonPlanId], action.payload.slide]
        }
      }
    })
    .addCase(REORDER_SLIDES_SUCCESS, (state, action) => {
      return { ...state, slides: { ...state.slides, [action.payload.lessonPlanId]: action.payload.slides } }
    })
    .addCase(SET_ACTIVE_SLIDE, (state, action) => {
      return { ...state, activeSlide: action.payload }
    })
    .addCase(SET_SLIDE_CONTENT, (state, action) => {
      if (!state.activeSlide) return state
      return { ...state, activeSlide: { ...state.activeSlide, html: action.payload } }
    })
    .addCase(SAVE_SLIDE_SUCCESS, (state, action) => {
      return {
        ...state,
        slides: {
          ...state.slides,
          [action.payload.lessonPlanId]: state.slides[action.payload.lessonPlanId].map((slide) =>
            slide.id === action.payload.slide.id ? action.payload.slide : slide
          )
        }
      }
    })
    .addCase(UPLOAD_IMAGE_SUCCESS, (state, action) => {
      return { ...state, uploadedImage: action.payload.imageUrl }
    })
    .addCase(CLEAR_UPLOADED_IMAGE, (state) => {
      return { ...state, uploadedImage: null }
    })
})
